import React, { useEffect, useRef, useState } from "react";
import { api, rupee, imageUrl } from "../lib/api";
import { useStores } from "../lib/stores";
import { calcPosBill } from "../lib/gst";
import { GST_STATES } from "../constants/gstStates";
import { ScanLine, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ROW_GRID =
  "grid w-full grid-cols-[48px_minmax(0,1.3fr)_68px_60px_minmax(0,0.9fr)_56px_80px_52px_44px_40px] items-center gap-x-3 px-4";

function pickProductImage(p) {
  if (Array.isArray(p.images) && p.images[0]) return p.images[0];
  if (Array.isArray(p.template_images) && p.template_images[0]) return p.template_images[0];
  return null;
}

const EMPTY_CUSTOMER = {
  billing_name: "",
  name: "",
  phone: "",
  gstin: "",
  billing_address: "",
  billing_city: "",
  billing_state: "",
  shipping_name: "",
  shipping_address: "",
  shipping_city: "",
  shipping_state: "",
};

export default function POS() {
  const [barcode, setBarcode] = useState("");
  const [items, setItems] = useState([]);
  const [customer, setCustomer] = useState(EMPTY_CUSTOMER);
  const [addressSection, setAddressSection] = useState("billing");
  const [discount, setDiscount] = useState(0);
  const [payment, setPayment] = useState("cash");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(false);
  const [lastBill, setLastBill] = useState(null);
  const { activeStoreId, stores } = useStores();
  const activeStore = stores.find((s) => s.id === activeStoreId) || stores.find((s) => s.is_default) || stores[0];
  const allowZeroStock = activeStore?.allow_zero_stock_bill === true;
  const priceIncludesGst = activeStore?.product_price_includes_gst !== false;
  const inputRef = useRef();

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (activeStore?.state) {
      setCustomer((c) => (c.billing_state ? c : { ...c, billing_state: activeStore.state }));
    }
  }, [activeStore?.id, activeStore?.state]);

  const storeState = activeStore?.state || "";

  const scan = async (e) => {
    e.preventDefault();
    if (!barcode) return;
    try {
      const { data: p } = await api.get(`/products/barcode/${barcode.trim()}`);
      if (p.status === "draft") { toast.error("Draft product — publish it before selling"); return; }
      if (!allowZeroStock && (p.stock <= 0 || p.status === "sold")) { toast.error("Out of stock"); return; }
      addItem(p);
      setBarcode("");
      setFlash(true); setTimeout(() => setFlash(false), 700);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Barcode not found");
    }
  };

  const addItem = (p) => {
    setItems((prev) => {
      const found = prev.find((x) => x.product_id === p.id);
      const nextQty = (found?.quantity || 0) + 1;
      if (!allowZeroStock && nextQty > (p.stock ?? 0)) {
        toast.error("Insufficient stock");
        return prev;
      }
      if (found) return prev.map((x) => x.product_id === p.id ? { ...x, quantity: x.quantity + 1, stock: p.stock } : x);
      return [...prev, {
        product_id: p.id,
        name: p.name,
        color: p.color || "",
        size: p.size || "",
        image: pickProductImage(p),
        unit_price: p.price,
        quantity: 1,
        discount: 0,
        barcode: p.barcode,
        stock: p.stock,
        gst_percent: p.gst_percent ?? 5,
        hsn_code: p.hsn_code || "",
      }];
    });
  };

  const removeItem = (id) => setItems((s) => s.filter((x) => x.product_id !== id));
  const setQty = (id, q) => {
    const qty = Math.max(1, Number(q) || 1);
    setItems((s) => {
      const item = s.find((x) => x.product_id === id);
      if (!allowZeroStock && item && qty > (item.stock ?? 0)) {
        toast.error("Insufficient stock");
        return s;
      }
      return s.map((x) => x.product_id === id ? { ...x, quantity: qty } : x);
    });
  };

  const bill = calcPosBill(items, discount, priceIncludesGst, storeState, customer.billing_state);
  const { grossTotal, subtotal, taxAmt, total, cgst, sgst, igst, isInterState } = bill;

  const checkout = async () => {
    if (items.length === 0) return toast.error("Add items first");
    if (!customer.billing_name?.trim()) return toast.error("Billing name is required");
    if (!customer.billing_state?.trim()) return toast.error("Billing state is required");
    setBusy(true);
    try {
      const billingName = customer.billing_name.trim();
      const payload = {
        items: items.map(({ product_id, quantity, unit_price, discount: lineDiscount }) => ({
          product_id,
          quantity,
          unit_price,
          discount: lineDiscount,
        })),
        discount: Number(discount || 0),
        payment_method: payment,
        billing_name: billingName,
        customer_name: customer.name?.trim() || undefined,
        customer_phone: customer.phone || undefined,
        customer_address: customer.billing_address || undefined,
        customer_city: customer.billing_city || undefined,
        customer_state: customer.billing_state,
        customer_gstin: customer.gstin?.trim() || undefined,
        shipping_name: customer.shipping_name?.trim() || undefined,
        shipping_address: customer.shipping_address || undefined,
        shipping_city: customer.shipping_city || undefined,
        shipping_state: customer.shipping_state || undefined,
        store_id: activeStore?.id || activeStoreId || undefined,
        notes: notes.trim() || undefined,
      };
      if (customer.phone) {
        try {
          await api.post("/customers", {
            name: customer.name?.trim() || billingName,
            phone: customer.phone,
            address: customer.billing_address || undefined,
            city: customer.billing_city || undefined,
            state: customer.billing_state,
            gstin: customer.gstin?.trim() || undefined,
          });
        } catch { /* ignore duplicates */ }
      }
      const { data } = await api.post("/sales", payload);
      setLastBill(data);
      toast.success(`Bill created: ${data.invoice_no}`);
      setItems([]);
      setCustomer({ ...EMPTY_CUSTOMER, billing_state: activeStore?.state || "" });
      setAddressSection("billing");
      setDiscount(0);
      setNotes("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto" data-testid="pos-root">
      <h1 className="font-display font-bold text-3xl mb-6">POS Billing</h1>
      <div className="grid lg:grid-cols-3 gap-5">
        {/* LEFT: scanner + items */}
        <div className="lg:col-span-2 space-y-4">
          <form onSubmit={scan} className={`card-soft p-4 flex items-center gap-3 ${flash ? "flash-success" : ""}`}>
            <div className={`w-11 h-11 rounded-md grid place-items-center scan-pulse`} style={{ background: "var(--surface-success)", color: "var(--success)" }}>
              <ScanLine size={22} />
            </div>
            <input
              ref={inputRef}
              data-testid="pos-barcode-input"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              placeholder="Scan or type barcode & press Enter"
              className="flex-1 h-12 px-3 rounded-md border bg-white text-lg num"
              style={{ borderColor: "var(--border-soft)" }}
            />
            <button data-testid="pos-scan-btn" className="h-12 px-5 rounded-md text-white font-semibold tactile-btn" style={{ background: "var(--primary)" }}>Add</button>
          </form>

          <div className="card-soft">
            <div className={`${ROW_GRID} py-3 text-xs font-medium uppercase tracking-wide`} style={{ color: "var(--text-muted)" }}>
              <div>Image</div>
              <div>Product</div>
              <div>Color</div>
              <div>Size</div>
              <div>Barcode</div>
              <div className="text-center">HSN</div>
              <div className="text-right">Price</div>
              <div className="text-center">Qty</div>
              <div className="text-center">GST</div>
              <div />
            </div>

            {items.length === 0 && (
              <div className="text-center py-12 text-sm" style={{ color: "var(--text-muted)" }}>
                Scan a barcode to start.
              </div>
            )}

            {items.map((it) => (
              <div
                key={it.product_id}
                className={`${ROW_GRID} py-3 border-t text-sm`}
                style={{ borderColor: "var(--border-soft)" }}
                data-testid={`pos-row-${it.barcode}`}
              >
                <div>
                  {it.image ? (
                    <img
                      src={imageUrl(it.image)}
                      alt=""
                      className="w-10 h-10 rounded-md border object-cover"
                      style={{ borderColor: "var(--border-soft)" }}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-md border" style={{ borderColor: "var(--border-soft)", background: "var(--surface-muted)" }} />
                  )}
                </div>
                <div className="font-medium leading-snug pr-2">{it.name}</div>
                <div className="text-sm truncate" style={{ color: "var(--text-muted)" }}>{it.color || "—"}</div>
                <div className="font-semibold">{it.size || "—"}</div>
                <div className="font-mono text-xs truncate">{it.barcode}</div>
                <div className="text-center font-mono text-xs truncate" style={{ color: "var(--text-muted)" }}>{it.hsn_code || "—"}</div>
                <div className="text-right num whitespace-nowrap">{rupee(it.unit_price)}</div>
                <div className="flex justify-center">
                  <input
                    type="number"
                    min="1"
                    value={it.quantity}
                    onChange={(e) => setQty(it.product_id, e.target.value)}
                    className="w-14 h-10 px-2 border rounded-md num text-center"
                    style={{ borderColor: "var(--border-soft)" }}
                    data-testid={`pos-qty-${it.barcode}`}
                  />
                </div>
                <div className="text-center num font-medium">{it.gst_percent ?? 5}%</div>
                <div className="flex justify-end">
                  <button data-testid={`pos-remove-${it.barcode}`} onClick={() => removeItem(it.product_id)} style={{ color: "var(--error)" }}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="card-soft p-4">
            <label className="text-sm font-medium" htmlFor="pos-notes">Notes</label>
            <textarea
              id="pos-notes"
              data-testid="pos-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional remarks for this bill…"
              rows={3}
              className="mt-2 w-full px-3 py-2.5 rounded-md border bg-white text-sm resize-y min-h-[5.5rem]"
              style={{ borderColor: "var(--border-soft)" }}
            />
          </div>
        </div>

        {/* RIGHT: customer + totals */}
        <div className="space-y-4">
          <div className="card-soft p-4">
            <div className="font-display font-semibold mb-3">Customer details</div>
            <div className="space-y-3">
              <input data-testid="pos-cust-phone" placeholder="Phone" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="h-12 w-full px-3 rounded-md border bg-white" style={{ borderColor: "var(--border-soft)" }} />
              <input
                data-testid="pos-cust-name"
                placeholder="Customer name"
                value={customer.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setCustomer({ ...customer, name, billing_name: name });
                }}
                className="h-12 w-full px-3 rounded-md border bg-white"
                style={{ borderColor: "var(--border-soft)" }}
              />
              <div>
                <label className="text-sm font-medium">Billing name *</label>
                <input
                  data-testid="pos-billing-name"
                  required
                  placeholder="Name on tax invoice"
                  value={customer.billing_name}
                  onChange={(e) => setCustomer({ ...customer, billing_name: e.target.value })}
                  className="mt-1 h-12 w-full px-3 rounded-md border bg-white"
                  style={{ borderColor: "var(--border-soft)" }}
                />
              </div>
              <div>
                <label className="text-sm font-medium">GSTIN <span className="font-normal" style={{ color: "var(--text-muted)" }}>(optional)</span></label>
                <input
                  data-testid="pos-cust-gstin"
                  placeholder="15-character GST number"
                  value={customer.gstin}
                  onChange={(e) => setCustomer({ ...customer, gstin: e.target.value.toUpperCase().replace(/\s/g, "") })}
                  className="mt-1 h-12 w-full px-3 rounded-md border bg-white font-mono text-sm"
                  style={{ borderColor: "var(--border-soft)" }}
                />
              </div>
              <div className="flex gap-4 pt-1">
                <label className="inline-flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input
                    type="radio"
                    name="pos-address-section"
                    value="billing"
                    checked={addressSection === "billing"}
                    onChange={() => setAddressSection("billing")}
                    data-testid="pos-addr-billing"
                  />
                  Billing Address
                </label>
                <label className="inline-flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input
                    type="radio"
                    name="pos-address-section"
                    value="shipping"
                    checked={addressSection === "shipping"}
                    onChange={() => setAddressSection("shipping")}
                    data-testid="pos-addr-shipping"
                  />
                  Shipping Address
                </label>
              </div>

              {addressSection === "billing" ? (
                <>
                  <input
                    data-testid="pos-cust-address"
                    placeholder="Billing address"
                    value={customer.billing_address}
                    onChange={(e) => setCustomer({ ...customer, billing_address: e.target.value })}
                    className="h-12 w-full px-3 rounded-md border bg-white"
                    style={{ borderColor: "var(--border-soft)" }}
                  />
                  <input
                    data-testid="pos-cust-city"
                    placeholder="Billing city"
                    value={customer.billing_city}
                    onChange={(e) => setCustomer({ ...customer, billing_city: e.target.value })}
                    className="h-12 w-full px-3 rounded-md border bg-white"
                    style={{ borderColor: "var(--border-soft)" }}
                  />
                  <div>
                    <label className="text-sm font-medium">Billing state *</label>
                    <select
                      data-testid="pos-cust-state"
                      required
                      value={customer.billing_state}
                      onChange={(e) => setCustomer({ ...customer, billing_state: e.target.value })}
                      className="mt-1 h-12 w-full px-3 rounded-md border bg-white"
                      style={{ borderColor: "var(--border-soft)" }}
                    >
                      <option value="">Select state</option>
                      {GST_STATES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    {storeState && customer.billing_state && (
                      <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                        {isInterState ? "Inter-state supply — IGST applies" : "Same state — CGST + SGST applies"}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <input
                    data-testid="pos-ship-name"
                    placeholder="Shipping name (optional)"
                    value={customer.shipping_name}
                    onChange={(e) => setCustomer({ ...customer, shipping_name: e.target.value })}
                    className="h-12 w-full px-3 rounded-md border bg-white"
                    style={{ borderColor: "var(--border-soft)" }}
                  />
                  <input
                    data-testid="pos-ship-address"
                    placeholder="Shipping address"
                    value={customer.shipping_address}
                    onChange={(e) => setCustomer({ ...customer, shipping_address: e.target.value })}
                    className="h-12 w-full px-3 rounded-md border bg-white"
                    style={{ borderColor: "var(--border-soft)" }}
                  />
                  <input
                    data-testid="pos-ship-city"
                    placeholder="Shipping city"
                    value={customer.shipping_city}
                    onChange={(e) => setCustomer({ ...customer, shipping_city: e.target.value })}
                    className="h-12 w-full px-3 rounded-md border bg-white"
                    style={{ borderColor: "var(--border-soft)" }}
                  />
                  <div>
                    <label className="text-sm font-medium">Shipping state</label>
                    <select
                      data-testid="pos-ship-state"
                      value={customer.shipping_state}
                      onChange={(e) => setCustomer({ ...customer, shipping_state: e.target.value })}
                      className="mt-1 h-12 w-full px-3 rounded-md border bg-white"
                      style={{ borderColor: "var(--border-soft)" }}
                    >
                      <option value="">Select state</option>
                      {GST_STATES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="card-soft p-4">
            <div className="font-display font-semibold mb-3">Bill summary</div>
            <div className="flex justify-between text-sm py-1">
              <span>Total amount</span>
              <span className="num">{rupee(grossTotal)}</span>
            </div>
            <div className="flex items-center justify-between text-sm py-1">
              <span>Discount ₹</span>
              <input data-testid="pos-discount-input" type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} className="w-24 h-9 text-right px-2 border rounded-md num" style={{ borderColor: "var(--border-soft)" }} />
            </div>
            <div className="flex justify-between text-sm py-1">
              <span>{priceIncludesGst ? "Taxable amount" : "Subtotal"}</span>
              <span className="num">{rupee(subtotal)}</span>
            </div>
            {isInterState ? (
              <div className="flex justify-between text-sm py-1">
                <span>IGST</span>
                <span className="num">{rupee(igst)}</span>
              </div>
            ) : (
              <>
                <div className="flex justify-between text-sm py-1">
                  <span>CGST</span>
                  <span className="num">{rupee(cgst)}</span>
                </div>
                <div className="flex justify-between text-sm py-1">
                  <span>SGST</span>
                  <span className="num">{rupee(sgst)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between text-sm py-1" style={{ color: "var(--text-muted)" }}>
              <span>Total GST</span>
              <span className="num">{rupee(taxAmt)}</span>
            </div>
            <hr className="my-3" style={{ borderColor: "var(--border-soft)" }} />
            <div className="flex justify-between items-baseline"><span className="font-display font-semibold">Grand total</span><span className="font-display font-bold text-3xl num" style={{ color: "var(--primary)" }}>{rupee(total)}</span></div>

            <div className="grid grid-cols-2 gap-2 mt-4">
              {["cash", "upi", "card", "pending"].map((m) => (
                <button key={m} onClick={() => setPayment(m)} data-testid={`pos-pay-${m}`} className={`h-11 rounded-md text-sm font-semibold border`} style={{ borderColor: payment === m ? "var(--primary)" : "var(--border-soft)", background: payment === m ? "var(--drag-highlight)" : "white", color: payment === m ? "var(--primary)" : "inherit" }}>{m.toUpperCase()}</button>
              ))}
            </div>

            <button disabled={busy || items.length === 0} data-testid="pos-checkout-btn" onClick={checkout} className="mt-4 w-full h-14 rounded-md text-white font-display font-bold text-lg tactile-btn" style={{ background: "var(--success)" }}>
              {busy ? "Saving..." : "Complete sale"}
            </button>
          </div>

          {lastBill && (
            <div className="card-soft p-4">
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>Last invoice</div>
              <div className="font-mono text-lg" data-testid="pos-last-invoice">{lastBill.invoice_no}</div>
              <div className="text-sm num">{rupee(lastBill.total)} · {lastBill.items.length} items</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
